/**
 * DetailPanel — 节点详情面板（投资分析师视角）
 *
 * Sprint 2 改造：
 * - 信息层级重写：header → 为什么重要 → 可投资公司Top3 → 上下游（折叠）
 * - 底部插入 DecisionBar（决策出口）
 * - 节点查看时自动标记为已勘察
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChainNode, ChainEdge, Company, FinancialRecord, Ranking } from '../../types';
import {
  BOTTLENECK_COLORS,
  TYPE_COLORS,
  TYPE_LABELS,
  QUADRANT_COLORS,
  QUADRANT_DOT_COLORS,
} from '../../utils/chain-constants';
import {
  isTollStation,
  isIrreplaceableNode,
  getUpstreamDownstream,
} from '../../utils/chain-helpers';
import { useExplorationStore } from '../../stores/explorationStore';
import DecisionBar from './DecisionBar';

interface DetailPanelProps {
  node: ChainNode;
  allNodes: ChainNode[];
  allEdges: ChainEdge[];
  allCompanies: Company[];
  allFinancials: FinancialRecord[];
  allRankings: Ranking[];
}

export default function DetailPanel({
  node, allNodes, allEdges, allCompanies, allFinancials, allRankings,
}: DetailPanelProps) {
  const markExplored = useExplorationStore(s => s.markExplored);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [showUpstreamDownstream, setShowUpstreamDownstream] = useState(false);

  // 查看节点时自动标记为已勘察（展开状态通过 key={node.id} 在父组件重置）
  useEffect(() => {
    markExplored(node.id);
  }, [node.id, markExplored]);

  const companies = useMemo(() => {
    const list = allCompanies.filter(c => node.companies.includes(c.company_id));
    return list.sort((a, b) => {
      // 可投资标的优先
      const aInvestable = a.cn_access_level === 'direct' || a.cn_access_level === 'indirect';
      const bInvestable = b.cn_access_level === 'direct' || b.cn_access_level === 'indirect';
      if (aInvestable && !bInvestable) return -1;
      if (!aInvestable && bInvestable) return 1;
      // 同类按 IPS 降序
      const ra = allRankings.find(r => r.company_id === a.company_id);
      const rb = allRankings.find(r => r.company_id === b.company_id);
      return (rb?.ips_score ?? 0) - (ra?.ips_score ?? 0);
    });
  }, [node.companies, allCompanies, allRankings]);

  const { upstream, downstream } = useMemo(() =>
    getUpstreamDownstream(node.id, allEdges, allNodes),
    [node.id, allEdges, allNodes],
  );

  const getLatestFinancial = useCallback((companyId: string) => {
    const records = allFinancials
      .filter(f => f.company_id === companyId)
      .sort((a, b) => (b.period || '').localeCompare(a.period || ''));
    return records.length > 0 ? records[0] : null;
  }, [allFinancials]);

  const getRanking = useCallback((companyId: string) => {
    return allRankings.find(r => r.company_id === companyId);
  }, [allRankings]);

  const bgColor = BOTTLENECK_COLORS[node.bottleneck_level] || '#475569';
  const investableCompanies = companies.filter(c => c.cn_access_level === 'direct' || c.cn_access_level === 'indirect');
  const displayCompanies = showAllCompanies ? companies : companies.slice(0, 3);

  return (
    <div style={{ padding: '14px', overflowY: 'auto', height: '100%', color: '#cbd5e1' }}>
      {/* ① Node header */}
      <h3 style={{ margin: '0 0 6px', fontSize: 15, color: '#f1f5f1', lineHeight: 1.3 }}>{node.name}</h3>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          padding: '2px 6px', borderRadius: 3, fontSize: 10,
          background: TYPE_COLORS[node.type] || '#475569', color: 'white',
        }}>
          {TYPE_LABELS[node.type] || node.type}
        </span>
        <span style={{
          padding: '2px 6px', borderRadius: 3, fontSize: 10,
          background: bgColor, color: 'white',
        }}>
          {node.bottleneck_level === 'high' ? '高瓶颈' : node.bottleneck_level === 'medium' ? '中瓶颈' : '低瓶颈'}
        </span>
        {isTollStation(node) && (
          <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, background: '#f59e0b', color: 'white', fontWeight: 700 }}>TOLL</span>
        )}
        {isIrreplaceableNode(node) && (
          <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, background: '#7c3aed', color: 'white' }}>不可替代</span>
        )}
      </div>

      {/* ② 为什么重要（合并 bottleneck_reason + 关键标签） */}
      {node.bottleneck_reason && (
        <div style={{
          fontSize: 12, color: '#fbbf24', background: 'rgba(180,83,9,0.15)',
          border: '1px solid rgba(180,83,9,0.3)',
          padding: '8px 10px', borderRadius: 5, marginBottom: 12, lineHeight: 1.5,
        }}>
          <div style={{ fontSize: 10, color: '#92400e', marginBottom: 4, fontWeight: 600 }}>为什么重要</div>
          {node.bottleneck_reason}
        </div>
      )}

      {/* ③ 可投资公司 Top3（首屏即见） */}
      <div style={{
        fontSize: 11, color: '#fbbf24', marginBottom: 6, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        关联公司 ({companies.length}{investableCompanies.length > 0 && ` · ${investableCompanies.length} 家可投资`}){companies.length > 0 && ' — 可投资优先，按 IPS 排序'}
      </div>
      {companies.length === 0 ? (
        <div style={{
          padding: '12px 8px', borderRadius: 5, border: '1px dashed #1e293b',
          background: '#0b1220', color: '#64748b', fontSize: 11, textAlign: 'center',
        }}>
          暂无关联公司
        </div>
      ) : displayCompanies.map(c => {
        const fin = getLatestFinancial(c.company_id);
        const rank = getRanking(c.company_id);
        const ips = rank?.ips_score ?? c.ips_score ?? 0;
        const qColor = rank?.quadrant ? QUADRANT_DOT_COLORS[rank.quadrant] : '#64748b';
        const qBg = rank?.quadrant ? QUADRANT_COLORS[rank.quadrant] : null;
        const investable = c.cn_access_level === 'direct' || c.cn_access_level === 'indirect';
        const accessLabel =
          c.cn_access_level === 'direct' ? 'A股/港股通' :
          c.cn_access_level === 'indirect' ? '间接' :
          c.cn_access_level === 'difficult' ? '难' : '仅参考';

        return (
          <div key={c.company_id} style={{
            padding: '7px 8px', marginBottom: 5, borderRadius: 5,
            border: `1px solid ${investable ? 'rgba(34,197,94,0.25)' : '#1e293b'}`,
            background: investable ? 'rgba(34,197,94,0.05)' : '#0b1220',
            fontSize: 11,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 12 }}>
                {investable && <span style={{ color: '#22c55e', marginRight: 4 }}>●</span>}
                {c.company}
              </span>
              <span style={{ color: '#64748b', fontSize: 10 }}>{c.ticker} · {c.exchange}</span>
            </div>

            {ips > 0 && (
              <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: '#64748b', minWidth: 28 }}>IPS</span>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#1e293b', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(ips, 100)}%`, height: '100%', borderRadius: 3,
                    background: ips >= 70 ? 'linear-gradient(90deg, #fbbf24, #f59e0b)' :
                                ips >= 50 ? 'linear-gradient(90deg, #3b82f6, #1d4ed8)' : '#475569',
                  }} />
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, minWidth: 28, textAlign: 'right',
                  color: ips >= 70 ? '#fbbf24' : ips >= 50 ? '#60a5fa' : '#64748b',
                }}>
                  {ips.toFixed(0)}
                </span>
              </div>
            )}

            <div style={{ marginTop: 4, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{
                padding: '1px 4px', borderRadius: 2, fontSize: 9,
                background: investable ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)',
                color: investable ? '#22c55e' : '#94a3b8',
                border: `1px solid ${investable ? 'rgba(34,197,94,0.3)' : 'rgba(100,116,139,0.2)'}`,
              }}>
                {accessLabel}
              </span>
              {rank?.tqs_score != null && (
                <span style={{ padding: '1px 4px', borderRadius: 2, fontSize: 9, background: 'rgba(59,130,246,0.15)', color: '#93c5fd' }}>
                  TQS {rank.tqs_score.toFixed(0)}
                </span>
              )}
              {rank?.mcs_score != null && (
                <span style={{ padding: '1px 4px', borderRadius: 2, fontSize: 9, background: 'rgba(245,158,11,0.15)', color: '#fcd34d' }}>
                  MCS {rank.mcs_score.toFixed(0)}
                </span>
              )}
              {rank?.quadrant && (
                <span style={{
                  padding: '1px 4px', borderRadius: 2, fontSize: 9,
                  background: qBg?.bg || `${qColor}20`, color: qBg?.text || qColor,
                }}>
                  {rank.quadrant}
                </span>
              )}
            </div>

            {fin && (
              <div style={{ color: '#64748b', marginTop: 3, fontSize: 10 }}>
                收入 {fin.revenue_latest?.toFixed(1)}{fin.currency === 'CNY' ? '亿' : fin.currency === 'USD' ? 'B' : ''}
                {fin.gross_margin != null && ` · 毛利率 ${fin.gross_margin.toFixed(1)}%`}
                {fin.revenue_yoy != null && ` · 同比 ${fin.revenue_yoy > 0 ? '+' : ''}${fin.revenue_yoy.toFixed(1)}%`}
              </div>
            )}
          </div>
        );
      })}

      {/* 公司展开/收起 */}
      {companies.length > 3 && (
        <button
          onClick={() => setShowAllCompanies(!showAllCompanies)}
          style={{
            width: '100%', padding: '6px', marginTop: 4,
            background: 'transparent', border: '1px dashed #334155',
            borderRadius: 4, color: '#64748b', cursor: 'pointer', fontSize: 10,
          }}
        >
          {showAllCompanies ? '收起' : `查看全部 ${companies.length} 家`}
        </button>
      )}

      {/* ④ 上下游（默认折叠） */}
      {(upstream.length > 0 || downstream.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setShowUpstreamDownstream(!showUpstreamDownstream)}
            style={{
              width: '100%', padding: '6px 8px',
              background: 'transparent', border: '1px solid #1e293b',
              borderRadius: 4, color: '#94a3b8', cursor: 'pointer', fontSize: 11,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span>上下游关系（上游 {upstream.length} · 下游 {downstream.length}）</span>
            <span>{showUpstreamDownstream ? '▼' : '▶'}</span>
          </button>
          {showUpstreamDownstream && (
            <div style={{ padding: '8px 0', position: 'relative' }}>
              <div style={{
                overflowX: 'auto', overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch', paddingBottom: 4,
              }}>
                {upstream.length > 0 && (
                  <div style={{ marginBottom: 6, minWidth: 'max-content' }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>上游 ({upstream.length})</div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {upstream.map(n => (
                        <span key={n.id} style={{
                          padding: '1px 5px', borderRadius: 3, fontSize: 10, whiteSpace: 'nowrap',
                          background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd',
                        }}>{n.name}</span>
                      ))}
                    </div>
                  </div>
                )}
                {downstream.length > 0 && (
                  <div style={{ minWidth: 'max-content' }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>下游 ({downstream.length})</div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {downstream.map(n => (
                        <span key={n.id} style={{
                          padding: '1px 5px', borderRadius: 3, fontSize: 10, whiteSpace: 'nowrap',
                          background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', color: '#c4b5fd',
                        }}>{n.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ④ 我的笔记历史（该节点所有笔记） */}
      <NodeNotesHistory nodeId={node.id} />

      {/* ⑤ 决策出口 */}
      <DecisionBar entityId={node.id} entityType="node" />
    </div>
  );
}

// ===== NodeNotesHistory — 该节点/公司的笔记历史（时间轴）=====

const TAG_EMOJI: Record<string, string> = {
  catalyst: '🚀',
  valuation: '📊',
  tech_lead: '⚡',
  industry_position: '🏰',
};

function NodeNotesHistory({ nodeId }: { nodeId: string }) {
  const notes = useExplorationStore(s => s.getNotesByEntity(nodeId));

  if (notes.length === 0) return null;

  return (
    <div style={{ marginTop: 12, marginBottom: 4 }}>
      <div style={{ fontSize: 11, color: '#fbbf24', fontWeight: 600, marginBottom: 8 }}>
        📝 我的笔记 ({notes.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {notes.map(note => (
          <div key={note.id} style={{
            padding: '8px 10px', borderRadius: 6,
            background: 'rgba(15,23,42,0.6)',
            border: note.status === 'conviction' ? '1px solid rgba(251,191,36,0.3)' :
                   note.status === 'watchlist' ? '1px solid rgba(16,185,129,0.3)' :
                   '1px solid #1e293b',
            fontSize: 11,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                  background: note.status === 'conviction' ? 'rgba(251,191,36,0.2)' :
                             note.status === 'watchlist' ? 'rgba(16,185,129,0.2)' : 'rgba(100,116,139,0.15)',
                  color: note.status === 'conviction' ? '#fbbf24' :
                         note.status === 'watchlist' ? '#10b981' : '#64748b',
                }}>
                  {note.status === 'conviction' ? '◆ 确信' :
                   note.status === 'watchlist' ? '● 观察' : '— 跳过'}
                </span>
                {note.tags.map(t => (
                  <span key={t} style={{ fontSize: 11 }}>{TAG_EMOJI[t] ?? ''}</span>
                ))}
              </div>
              <span style={{ color: '#64748b', fontSize: 10 }}>
                {new Date(note.timestamp).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {note.reason && (
              <div style={{ color: '#e2e8f0', lineHeight: 1.5 }}>{note.reason}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
